import React, { useMemo } from 'react';
import { BodyS, BodySBold } from 'components/Typography/Typography';
import styles from 'assets/jss/material-dashboard-pro-react/components/sidebarSearchStyle';
import { makeStyles } from '@material-ui/core/styles';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import classNames from 'classnames';
import { DROPDOWN_MAX_HEIGHT, MENU_ITEM_HEIGHT } from '../const';
import CustomLink from 'components/CustomLink/CustomLink';
import { SingleSearchNumber } from 'utils/api/models/search/singleSearchNumber';
import { SingleSearchBillingAccount } from 'utils/api/models/search/singleSearchBillingAccount';
import { AccountType } from 'utils/api/models/accounts/accountType';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { getLanguage } from 'utils/language/language';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ISingleSuggestionProps {
	suggestion: types.search.ISingleSuggestion;
	query: string;
	active: boolean;
	userDetailsPath: (userId: string) => string;
	numberDetailsPath: (suggestion: SingleSearchNumber) => string;
	billingAccountDetailsPath: (suggestion: SingleSearchBillingAccount) => string;
	isNewDashBoardEnabled: boolean;
	accId: string;
	searchInputValue: string;
}

const SingleSuggestion = ({
	suggestion,
	query,
	active,
	userDetailsPath,
	numberDetailsPath,
	billingAccountDetailsPath,
	isNewDashBoardEnabled,
	accId,
	searchInputValue,
}: ISingleSuggestionProps) => {
	const classes = useStyles({
		dropdownMaxHeight: DROPDOWN_MAX_HEIGHT,
		menuItemHeight: MENU_ITEM_HEIGHT,
		newDashBoardEnabled: isNewDashBoardEnabled,
	});
	const singleSuggestionClasses = classNames(classes.singleSuggestion, {
		[classes.activeSuggestion]: active,
	});

	const text = useMemo(() => {
		if (suggestion.type === 'USER') {
			return suggestion.suggestion.userName;
		}
		if (suggestion.type === 'NUMBER') {
			return suggestion.suggestion.accountNum;
		}
		if (suggestion.type === 'BILLING_ACCOUNT') {
			return suggestion.suggestion.billingId;
		}
		return '';
	}, [suggestion]);

	const index: number = useMemo(() => {
		return query ? text.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) : -1;
	}, [query, text]);

	const path: string = useMemo(() => {
		if (suggestion.type === 'USER') {
			return userDetailsPath(suggestion.suggestion.userId);
		}
		if (suggestion.type === 'NUMBER') {
			return numberDetailsPath(suggestion.suggestion);
		}
		if (suggestion.type === 'BILLING_ACCOUNT') {
			return billingAccountDetailsPath(suggestion.suggestion);
		}
		return '';
	}, [suggestion, userDetailsPath, numberDetailsPath, billingAccountDetailsPath]);

	return (
		<div className={singleSuggestionClasses}>
			<CustomLink
				to={path}
				props={{
					accountCategory:
						suggestion.type === 'NUMBER' && suggestion.suggestion.accountType === AccountType.INTERNET
							? suggestion.suggestion.accountCategory
							: '',
				}}
				linkProps={{
					onClick: () => {
						setDataLayer('destination_search_result', getLanguage(), null, accId, null, {
							search_term: searchInputValue,
							dest_url: path,
						});
					},
				}}
			>
				<div>
					<ConditionalRender
						show={index >= 0}
						onTrue={() => (
							<>
								<BodyS
									color={isNewDashBoardEnabled ? 'primary-light' : 'primary-dark'}
									disableMargin
									inline
								>
									{text.substring(0, index)}
								</BodyS>
								<BodySBold color="brand" disableMargin inline>
									{text.substring(index, index + query!.length)}
								</BodySBold>
								<BodyS
									color={isNewDashBoardEnabled ? 'primary-light' : 'primary-dark'}
									disableMargin
									inline
								>
									{text.substring(index + query!.length)}
								</BodyS>
							</>
						)}
						onFalse={() => (
							<BodyS color={isNewDashBoardEnabled ? 'primary-light' : 'secondary-dark'} disableMargin>
								{text}
							</BodyS>
						)}
					/>
				</div>
			</CustomLink>
		</div>
	);
};

export default SingleSuggestion;
